import { useState, useEffect } from 'react';
import { apiFetch } from './api.js';

function normalize(res) {
  if (Array.isArray(res)) return res;
  return res?.data ?? res?.priorities ?? [];
}

const stores = {};

function getStore(path) {
  if (!stores[path]) stores[path] = { data: [], promise: null, listeners: new Set() };
  return stores[path];
}

function ensureLoaded(path) {
  const store = getStore(path);
  if (!store.promise) {
    store.promise = apiFetch(path)
      .then(res => { store.data = normalize(res); })
      .catch(() => { store.data = []; })
      .finally(() => { store.listeners.forEach(fn => fn(store.data)); });
  }
  return store;
}

// Self-contained lookup fetch, independent of LookupsContext. A Context only
// bridges consumers built into the SAME bundle as its Provider - a
// dynamically-loaded extension is a separate Vite build, so its useContext()
// calls never see the host app's <LookupsProvider> and silently get the
// Context's default (empty) value instead (see FilterPill.jsx for the same
// issue on the i18n side). Components that must render correctly wherever
// they're used (StatusChip, TypeChip, ...) call this as a fallback so they're
// not at the mercy of which bundle happened to load them. One in-flight
// request per path per loaded bundle - concurrent consumers all read the same
// module-level cache instead of each firing their own fetch.
export function useSelfLookup(path, enabled) {
  const store = getStore(path);
  const [data, setData] = useState(store.data);

  useEffect(() => {
    if (!enabled) return undefined;
    ensureLoaded(path);
    if (store.data.length) setData(store.data);
    store.listeners.add(setData);
    return () => store.listeners.delete(setData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled]);

  return data;
}
